import type { BoardElement } from './boards'
import type { LibraryDocument } from './library'
import type { Note } from './notes'
import { malformed, readJson, request } from './request'
import type { Task } from './tasks'
import type { Flashcard } from './flashcards'
import type { Subject } from './subjects'
import type { Appointment } from './timetable'

/**
 * The search, as the service describes it.
 *
 * Three things travel with every answer, and none of them is worked out a second time on this side —
 * which is the point of the module being this thin:
 *
 * - **how the line was read.** Which parts of it were filters, what they mean in words, and where a
 *   token stands that nothing understood. The rules of the search language live in the service, so the
 *   reading line and the wavy underline are *drawn* here and decided there. Two grammars, one of them in
 *   TypeScript, would drift apart within a month.
 * - **what to suggest**, while the cursor stands inside a filter token — and nothing otherwise. The
 *   values come out of the same registry that reads them, so a filter that is added later appears in the
 *   suggestions on its own.
 * - **how far the reading of the space has got**, so that both surfaces can say quietly that the results
 *   are not yet the whole truth. It comes with the search rather than from a second request: Spotlight
 *   opens, types and shows a hit list in one round trip.
 */

/** Where a found word begins and ends inside a passage. Two control characters, deliberately not HTML. */
export const HIT_START = '\u0002'
export const HIT_END = '\u0003'

/**
 * One hit, and the thing it points at.
 *
 * **Exactly one of {@link document}, {@link note} and {@link task} is filled**, and {@link kind} says
 * which. The three have almost nothing in common to draw — one has a picture, a folder, a kind of file
 * and where it came from; the second a few lines of markup; the third one sentence with a day and a
 * tick — so each is drawn as what it is rather than squeezed into a shape they share. A diary entry and
 * a card on a board become a fourth and a fifth field here.
 */
export interface SearchHit {
  /** `document`, `note`, `task` or `board-element`. A diary entry later. */
  readonly kind: string
  /**
   * The passage the words were found in, with each find between {@link HIT_START} and {@link HIT_END}.
   *
   * Not HTML: markup on its way through JSON into a page has to be either trusted or escaped, and a page
   * that trusts it renders part of somebody's document as part of itself the first time a PDF contains
   * the word `<script>`.
   */
  readonly snippet: string
  /** The document itself, exactly as the library describes it — so a hit is drawn like a tile. */
  readonly document: LibraryDocument | null
  /** The note itself, exactly as the pad describes it. */
  readonly note: Note | null
  /** The task itself, exactly as the list describes it. */
  readonly task: Task | null
  /** The learning card itself, with both sides and its tags. */
  readonly flashcard: Flashcard | null
  /**
   * Something written on a board, together with the board it lies on.
   *
   * The board travels with it because a hit on a board leads to a **place on a surface**, and the
   * element alone does not say where that is. A *card* is deliberately never a hit: what it points
   * at is already in the index under its own words, and a second entry would be the same passage
   * twice.
   */
  readonly boardElement: BoardHit | null
  /**
   * A subject: the bracket itself, leading to its overview.
   *
   * One hit and never one per document under it — everything that belongs to a subject is in the
   * index already, under its own words.
   */
  readonly subject: Subject | null
  /** One entry in the timetable — the anchor a session's material hangs off. */
  readonly appointment: Appointment | null
}

/** Something written on a board, and where it lies — everything the way there is made of. */
export interface BoardHit {
  readonly boardId: string
  readonly boardName: string
  readonly element: BoardElement
}

/**
 * Whether this hit points at a note.
 *
 * A field that is missing altogether counts as absent, not as present-and-empty: a service older than
 * the notepad sends no `note` at all, and a hit from it is a document.
 */
export function isNote(hit: SearchHit): boolean {
  return hit.note !== null && hit.note !== undefined
}

/** The other half of the same question. */
export function isDocument(hit: SearchHit): boolean {
  return hit.document !== null && hit.document !== undefined
}

/** And the third. Absent counts as absent here too, for a service older than the tasks. */
export function isTask(hit: SearchHit): boolean {
  return hit.task !== null && hit.task !== undefined
}

export function isFlashcard(hit: SearchHit): boolean {
  return hit.flashcard !== null && hit.flashcard !== undefined
}

/** The fifth: the bracket itself. */
export function isSubject(hit: SearchHit): boolean {
  return hit.subject !== null && hit.subject !== undefined
}

/** And the sixth: one entry in the timetable. */
export function isAppointment(hit: SearchHit): boolean {
  return hit.appointment !== null && hit.appointment !== undefined
}

/** And the fourth: something written on a board. */
export function isOnABoard(hit: SearchHit): boolean {
  return hit.boardElement !== null && hit.boardElement !== undefined
}

/** One filter the service understood, and what it means in words. */
export interface ReadFilter {
  readonly key: string
  /** What it is called in the reading line: `Folder`, `Created`. */
  readonly label: string
  /** What was written after the colon. */
  readonly value: string
  /** What it means, spelled out: `November 2025`. */
  readonly reading: string
  readonly from: number
  readonly to: number
}

/** Where something stands in the line: from this character up to, but not including, that one. */
export interface Range {
  readonly from: number
  readonly to: number
}

/** How MyLib read the line — everything the quiet line underneath the field is drawn from. */
export interface Reading {
  /** What is left over as words to search for. */
  readonly text: string
  readonly filters: readonly ReadFilter[]
  /** Tokens no filter understood. They count as ordinary search text and are marked as such. */
  readonly unreadable: readonly Range[]
}

export interface Suggestion {
  /** What is written into the line when it is taken, quoted where it has to be. */
  readonly value: string
  readonly label: string
}

/**
 * The filter token the cursor is standing in.
 *
 * `null` whenever it is not, and that is what decides whether suggestions replace the hits: while
 * `folder:Sem` is half typed the question is half asked, and hits for half a question are hits for
 * something nobody is looking for.
 */
export interface Typing {
  readonly from: number
  readonly to: number
  /** The filter being typed, or `null` while the key itself is still half written. */
  readonly key: string | null
  readonly suggestions: readonly Suggestion[]
}

/** How much of the space is searchable. What both surfaces say quietly while something is outstanding. */
export interface IndexProgress {
  readonly total: number
  readonly indexed: number
  /** Waiting to be read, or being read — one number, because both mean "not findable yet". */
  readonly outstanding: number
  readonly withoutText: number
  readonly failed: number
  /** Whether what a search answers right now is the whole truth. */
  readonly complete: boolean
}

export interface Found {
  /** How many hits there are altogether, of which {@link hits} is one page. */
  readonly total: number
  readonly hits: readonly SearchHit[]
  readonly reading: Reading
  readonly typing: Typing | null
  readonly index: IndexProgress
}

export type SearchOrder = 'relevance' | 'newest'

export interface Asked {
  /** The whole search line, filter tokens and all. There are no separate parameters for the filters. */
  readonly line: string
  /** Where the cursor is, so the service can say what is being typed. */
  readonly caret?: number
  readonly order?: SearchOrder
  readonly limit?: number
  readonly offset?: number
}

const SEARCH = '/api/search'

/** How many hits one page carries. The service decides the same number; this is what it asks for. */
export const PAGE = 25

export async function search(asked: Asked, signal?: AbortSignal): Promise<Found> {
  const query = new URLSearchParams({ q: asked.line })
  if (asked.caret !== undefined) {
    query.set('caret', String(asked.caret))
  }
  if (asked.order !== undefined) {
    query.set('sort', asked.order)
  }
  query.set('limit', String(asked.limit ?? PAGE))
  query.set('offset', String(asked.offset ?? 0))

  const payload = await readJson<Partial<Found>>(await request(`${SEARCH}?${query}`, { signal }))

  if (!Array.isArray(payload.hits) || typeof payload.total !== 'number') {
    throw malformed('The answer did not describe a search.')
  }

  return {
    total: payload.total,
    hits: payload.hits,
    reading: payload.reading ?? { text: asked.line, filters: [], unreadable: [] },
    typing: payload.typing ?? null,
    index: payload.index ?? NOTHING_OUTSTANDING,
  }
}

export async function fetchIndexProgress(signal?: AbortSignal): Promise<IndexProgress> {
  const payload = await readJson<Partial<IndexProgress>>(
    await request(`${SEARCH}/index`, { signal }),
  )
  if (typeof payload.total !== 'number') {
    throw malformed('The answer did not describe how far the reading has got.')
  }
  return { ...NOTHING_OUTSTANDING, ...payload } as IndexProgress
}

/**
 * Asks for everything in the space to be read again.
 *
 * The answer is how much is now waiting, so the progress line moves the moment the control is used
 * rather than at the next poll.
 */
export async function rebuildIndex(): Promise<IndexProgress> {
  const response = await request(`${SEARCH}/index/rebuild`, { method: 'POST' })
  return (await readJson<IndexProgress>(response)) as IndexProgress
}

const NOTHING_OUTSTANDING: IndexProgress = {
  total: 0,
  indexed: 0,
  outstanding: 0,
  withoutText: 0,
  failed: 0,
  complete: true,
}

/**
 * A passage cut into the parts that were found and the parts that were not.
 *
 * The alternative would be `dangerouslySetInnerHTML`, and there is no version of that which is safe with
 * text out of somebody's PDF. Splitting on two control characters costs three lines and cannot render
 * anything.
 */
export function partsOf(snippet: string): { readonly text: string; readonly found: boolean }[] {
  return snippet
    .split(HIT_START)
    .flatMap((part, index) => {
      if (index === 0) {
        return part === '' ? [] : [{ text: part, found: false }]
      }
      const [found = '', rest = ''] = part.split(HIT_END)
      const parts = [{ text: found, found: true }]
      return rest === '' ? parts : [...parts, { text: rest, found: false }]
    })
    .filter((part) => part.text !== '')
}

/** What a document's text state means for somebody looking at it — `null` when there is nothing to say. */
export function whyItMightNotBeFound(document: LibraryDocument): string | null {
  switch (document.textState) {
    case 'without-text':
      return 'No text could be read from this document, so it is only found by its title. A photograph or a scan becomes searchable once its text has been recognised.'
    case 'failed':
      return 'This document could not be read, so it is only found by its title.'
    case 'pending':
    case 'indexing':
      return 'This document is still being read, so its content is not searchable yet.'
    default:
      return null
  }
}
